import React, { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Formik } from "formik";
import * as Yup from "yup";
import { updateMeetTheTeam, getByMeetTheTeam, createMeetTheTeam } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required("Pet name is required"),
  owner: Yup.string().required("Owner name is required"),
  position: Yup.string().required("Owner's position is required"),
  image: Yup.mixed().required("Image is required"),
});

export default function MeetTeamEdit() {
  const { id, partnerId } = useParams();
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const inputRef1 = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const formikRef = useRef(null);
  const initialFormData = useRef({
    name: "",
    owner: "",
    position: "",
    image: "",
    originalImage: "",
  });

  const getByMeetTheTeamById = async () => {
    try {
      const res = await getByMeetTheTeam(id);
      const meetTeamData = res.data.data[0];
      
      // Update the ref with fetched data
      initialFormData.current = {
        name: meetTeamData.name || "",
        owner: meetTeamData.owner || "",
        position: meetTeamData.position || "",
        image: meetTeamData.image || "",
        originalImage: meetTeamData.image || "",
      };
      
      // Reset Formik with new data if component is mounted
      if (formikRef.current) {
        formikRef.current.resetForm({
          values: initialFormData.current
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Error loading data.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getByMeetTheTeamById();
  }, [id]);

  const handleImageChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue("image", file);
    }
  };

  const handleImageDelete = (setFieldValue, values) => {
    // If editing existing entry and there's an original image, revert to it
    if (Number(id) && values.originalImage) {
      setFieldValue("image", values.originalImage);
    } else {
      // For new entries or when no original image exists, clear the field
      setFieldValue("image", "");
    }
    
    // Reset file input
    if (inputRef1.current) {
      inputRef1.current.value = '';
    }
  };

  const handleExistingImageDelete = (setFieldValue) => {
    // Clear both image and originalImage
    setFieldValue("image", "");
    setFieldValue("originalImage", "");
    
    // Reset file input
    if (inputRef1.current) {
      inputRef1.current.value = '';
    }
  };

  const handleSubmit = async (values, { setFieldValue }) => {
    setIsUpdating(true);
    const formData = new FormData();
    
    formData.append("name", values.name);
    formData.append("owner", values.owner);
    formData.append("position", values.position);
    
    if (values.image instanceof File) {
      formData.append("image", values.image);
    }

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const response = await createMeetTheTeam(formData);
        if (response.status === 200) {
          const teamData = response.data.data;
          // Update Formik state with new data
          setFieldValue("image", teamData.image);
          setFieldValue("originalImage", teamData.image);
          showToast({
            type: 'success',
            message: response?.data?.message || "Meet the team section has been created successfully!",
          });
          navigate(`/${partnerId}/aboutus/meet_the_team/edit/${teamData.id}`);
        } else {
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to create meet the team section. Please try again.",
          });
        }
        setIsUpdating(false);
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to create meet the team section. Please try again.',
        });
        console.error(error);
        setIsUpdating(false);
      }
    } else {
      try {
        const response = await updateMeetTheTeam(id, formData);
        if (response.status === 200) {
          // Update originalImage after successful update
          if (values.image instanceof File) {
            // If we uploaded a new file, the response should contain the new image URL
            const updatedImageUrl = response.data.data?.image || values.image;
            setFieldValue("originalImage", updatedImageUrl);
            setFieldValue("image", updatedImageUrl);
          }
          showToast({
            type: 'success',
            message: response?.data?.message || "Meet the team section has been updated successfully!",
          });
        } else {
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to update meet the team section. Please try again.",
          });
        }
        setIsUpdating(false);
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to update meet the team section. Please try again.',
        });
        console.error(error);
        setIsUpdating(false);
      }
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress /> : 
        <FormWrapper>
          <Formik
            innerRef={formikRef}
            initialValues={initialFormData.current}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={false}
          >
            {({ handleSubmit, getFieldProps, setFieldValue, values, errors, touched }) => (
              <Form onSubmit={handleSubmit} noValidate>
                {/* Hidden field to track original image */}
                <input type="hidden" name="originalImage" value={values.originalImage} />
                
                <Title>{Number(id) ? 'Edit' : 'Add'} Meet the Team Section</Title>
                <SliderDiv>
                  <SubTitle>{Number(id) ? 'Edit' : 'Add'} Members</SubTitle>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup 
                        controlId="MeetTheTeamControl" 
                        label="Team Member Image"
                        requiredField
                        error={touched.image && errors.image}
                      >
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange(e, setFieldValue)}
                          style={{ display: "none" }}
                          ref={inputRef1}
                          name="image"
                        />
                        
                        {/* Show uploaded image (File object) */}
                        {values.image instanceof File ? (
                          <ImageWrapper>
                            <BootstrapImage
                              src={URL.createObjectURL(values.image)}
                              alt="uploaded-image"
                              onClick={() => inputRef1.current.click()}
                            />
                            <RemoveButtonWrapper>
                              <BootstrapButton
                                type="button"
                                onClick={() => handleImageDelete(setFieldValue, values)}
                              >
                                <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                              </BootstrapButton>
                            </RemoveButtonWrapper>
                          </ImageWrapper>
                        ) : (
                          /* Show existing image (URL string) */
                          values.image && typeof values.image === 'string' ? (
                            <ImageWrapper>
                              <BootstrapImage
                                src={values.image}
                                alt="existing-image"
                                onClick={() => inputRef1.current.click()}
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = 0.8;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = 1;
                                }}
                              />
                              {values.image instanceof File || (values.image && values.image !== values.originalImage) && (
                                <RemoveButtonWrapper>
                                  <BootstrapButton
                                    type="button"
                                  onClick={() => handleExistingImageDelete(setFieldValue)}
                                >
                                  <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                                </BootstrapButton>
                              </RemoveButtonWrapper>
                              )}
                            </ImageWrapper>
                          ) : (
                            /* Show upload button when no image */
                            <Button 
                            className="mb-0"
                              type="button"
                              onClick={() => inputRef1.current.click()}
                              variant={'primary'}
                            > 
                              Upload the image 
                            </Button>
                          )
                        )}
                      </FormGroup>
                    </Col>
                    
                    <Col style={{ marginTop: "1.2vw" }}>
                      <FormGroup 
                        controlId="TeamPetName" 
                        label="Pet Name"
                        requiredField
                        error={errors.name}
                      >
                        <Form.Control
                          type="text"
                          {...getFieldProps("name")}
                          isInvalid={touched.name && errors.name}
                        />
                      </FormGroup>
                 
                      <FormGroup 
                        controlId="TeamPetOwner" 
                        label="Owner"
                        requiredField
                        error={errors.owner}
                      >
                        <Form.Control
                          type="text"
                          {...getFieldProps("owner")}
                          isInvalid={touched.owner && errors.owner}
                        />
                      </FormGroup>
                   
                      <FormGroup 
                        controlId="TeamOwnerPosition" 
                        label="Owner's Position"
                        requiredField
                        error={errors.position}
                      >
                        <Form.Control
                          type="text"
                          {...getFieldProps("position")}
                          isInvalid={touched.position && errors.position}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  
                  {/* Button Section */}
                  <ButtonWrapper>
                    <Link onClick={() => navigate(-1)}>
                      <Button variant="secondary">Go Back</Button>
                    </Link>
                    <div>
                      <Button type="submit" disabled={isUpdating}>
                        {isUpdating ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </ButtonWrapper>
                </SliderDiv>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      }
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const FormWrapper = styled.div`
  @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h6``;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  gap: 2rem;
   justify-content: end;
`;
const ImageWrapper = styled.div`
    position:relative;
    max-width: 13vw;
   @media (max-width:576px) {
   max-width: 60vw;
  }
`;
const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
   position: absolute;
    top: -17px;
    right: -14px;
    @media (max-width:576px) {
     top: -9px;
    right: -17px;}
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "13vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "60vw",
  },
}));
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));